import {
  FC,
  useContext,
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
} from "react";
import Cookies from "js-cookie";
import { useIntl } from "react-intl";
import PageHead from "./components/pageHead";
import { Box, Typography } from "@mui/material";
import { Loader } from "@repo/ui";
import { AudienceContext } from "@repo/contexts";
import { buildQueryString, getQueryParam, buildHref } from "@repo/utils";
import { clearOktaAuths } from "@repo/auth-config";
import { useNavigate } from "react-router-dom";

// Constants
const ERROR_REDIRECT_DELAY_MS = 2000;

/**
 * LogOut component handles the logout process including:
 * - Clearing session cookies
 * - Clearing Okta authentication
 * - Applying full-page styles
 * - Redirecting to appropriate login page
 */
const LogOut: FC = () => {
  const navigate = useNavigate();
  const { paths } = useContext(AudienceContext);
  const intl = useIntl();
  const [isClearing, setIsClearing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Extract logic into separate functions for better readability
  const isExpired = getQueryParam("expired") === "true";

  /**
   * Applies full-page styles for the logout process and returns a cleanup function
   */
  const applyFullPageStyles = useCallback(() => {
    const de = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");

    if (!root) {
      return () => {}; // Safety check
    }

    // Store original styles
    const originalStyles = {
      de: { height: de.style.height, margin: de.style.margin },
      body: { height: body.style.height, margin: body.style.margin },
      root: {
        display: root.style.display,
        flexDirection: root.style.flexDirection,
        height: root.style.height,
      },
    };

    // Apply full page styles
    const fullPageStyles = {
      height: "100%",
      margin: "0",
    };

    Object.assign(de.style, fullPageStyles);
    Object.assign(body.style, fullPageStyles);
    Object.assign(root.style, {
      display: "flex",
      flexDirection: "column",
      height: "100%",
    });

    // Return cleanup function
    return () => {
      Object.assign(de.style, originalStyles.de);
      Object.assign(body.style, originalStyles.body);
      Object.assign(root.style, originalStyles.root);
    };
  }, []);

  /**
   * Clears session-related cookies
   */
  const clearSessionCookies = useCallback((): void => {
    try {
      Cookies.remove("UUID", { path: "/", secure: true });
      Cookies.remove("inst-nc", { path: "/" });
    } catch {
      throw new Error("Failed to clear session cookies");
    }
  }, []);

  /**
   * Clears Okta authentication state
   */
  const clearOktaAuthentication = useCallback(async (): Promise<void> => {
    try {
      const { institutionalAuth } = await clearOktaAuths();
      institutionalAuth.idx.clearTransactionMeta();
    } catch {
      throw new Error("Failed to clear Okta authentication");
    }
  }, []);

  /**
   * Redirects to the appropriate logout or login page based on session expiry
   */
  const performRedirect = useCallback((): void => {
    const queryParams = buildQueryString(["next", "region"]);
    if (!isExpired) {
      // TODO: encode URI components if necessary
      window.location.replace(
        `${window.location.origin}${buildHref(paths.logoutLegacyAuth, queryParams)}`,
      );
      return;
    }

    navigate(`${buildHref(paths.login, queryParams)}`);
  }, [isExpired, navigate, paths.logoutLegacyAuth, paths.login]);

  /**
   * Main logout orchestration function that handles the complete logout process
   */
  const clearSession = useCallback(async (): Promise<void> => {
    try {
      setIsClearing(true);
      setError(null);

      // Clear session cookies
      clearSessionCookies();

      // Clear Okta authentication
      await clearOktaAuthentication();

      // Perform redirect
      performRedirect();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      console.error("Logout process failed:", errorMessage);
      setError(errorMessage);
      setIsClearing(false);

      // Fallback redirect after error
      setTimeout(() => {
        performRedirect();
      }, ERROR_REDIRECT_DELAY_MS);
    }
  }, [clearSessionCookies, clearOktaAuthentication, performRedirect]);

  // Apply full page styles on mount
  useLayoutEffect(() => {
    const cleanup = applyFullPageStyles();
    return cleanup;
  }, [applyFullPageStyles]);

  // Start logout process on mount
  useEffect(() => {
    clearSession();
  }, [clearSession]);

  return (
    <>
      <PageHead
        title={intl.formatMessage({ id: "logout" })}
        description={intl.formatMessage({ id: "logoutDescription" })}
      />
      <Loader isFullPage />
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <Box sx={{ textAlign: "center", padding: 2.5 }}>
          <Typography variant="body1" sx={{ mt: 1 }}>
            {error
              ? "Redirecting in a moment..."
              : isClearing
                ? "Logging out..."
                : "Redirecting..."}
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default LogOut;
